/** Renders the launcher and the chat panel inside the shadow tree (ADR-0059). */
import { styles } from './styles.js';
import { fetchConversation, forgetThread, isResuming, lastActivity, loadThreadId, rememberOpen, sendMessage, wasOpen, REPLY_TIMEOUT_MS } from './conversation.js';
import { chatSession, declineConsent, withdrawConsent } from './session.js';
import { current as storedConsent, declined as wasDeclined, forget as forgetConsent, newId, remember as rememberConsent, rememberDeclined, renderConsentHtml, whenRequired } from './consent.js';
import { createLauncher, greetingMemory } from './launcher.js';
import { unreadTracker } from './unread.js';
import { translator } from './copy.js';
import { reportHref } from './report.js';
import { FONT_FAMILY, withFallback } from './assets.js';
import { appendRichText } from './rich-text.js';
import { describeHours, opening } from './hours.js';
import { forgetLive, leaveMessage, liveConnection, liveStatus, openSupportTicket, requestHandoff, storedLive } from './live-chat.js';
import { MAX_SECONDS, startRecording, transcribe, voiceSupported } from './voice.js';
import { sendFeedback } from './feedback.js';
import { controlFor, normalizeContactForm, whatsappUrl } from './contact.js';

// ADR-0056: the backend rejects longer questions; the counter appears near it.
const MAX_LENGTH = 1000;
const COUNTER_FROM = 800;

function el(tag, className, attributes = {}) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  for (const [name, value] of Object.entries(attributes)) node.setAttribute(name, value);
  return node;
}

// Remembered for the site, whatever its chatbot: the visitor muted it once.
const SOUND_KEY = 'chatbot_sound_muted';
let soundOverride = null;
function soundMuted() {
  if (soundOverride !== null) return soundOverride;
  try { return window.localStorage.getItem(SOUND_KEY) === '1'; } catch { return false; }
}
function setSoundMuted(muted) {
  try {
    if (muted) window.localStorage.setItem(SOUND_KEY, '1');
    else window.localStorage.removeItem(SOUND_KEY);
  } catch { /* the switch still works for this page */ }
  soundOverride = muted;
}

function icon(d, size = 20) {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  for (const [name, value] of Object.entries({
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true',
  })) svg.setAttribute(name, value);
  for (const segment of [].concat(d)) {
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', segment);
    svg.appendChild(path);
  }
  return svg;
}

const ICON_CLOSE = 'M6 6l12 12M18 6L6 18';
const ICON_NEW = ['M12 5v14', 'M5 12h14'];
const ICON_BELL = ['M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9', 'M10.3 21a1.94 1.94 0 0 0 3.4 0'];
const ICON_BELL_OFF = ['M8.7 3A6 6 0 0 1 18 8a21.3 21.3 0 0 0 .6 5', 'M17 17H3s3-2 3-9a4.67 4.67 0 0 1 .3-1.7', 'M10.3 21a1.94 1.94 0 0 0 3.4 0', 'M2 2l20 20'];
const ICON_PERSON = ['M12 12a4 4 0 1 0 0-8a4 4 0 0 0 0 8', 'M4 20c0-3.3 3.6-6 8-6s8 2.7 8 6'];
const ICON_SEND = 'M5 12h14M13 6l6 6-6 6';
const ICON_ALERT = ['M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18', 'M12 8v5M12 16.5v.5'];
const ICON_SHIELD = ['M12 3l7 3v5c0 4.5-3 8.2-7 10c-4-1.8-7-5.5-7-10V6z', 'M9 12l2 2 4-4'];
const ICON_MIC = ['M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3', 'M19 11a7 7 0 0 1-14 0', 'M12 18v3'];
const ICON_CHECK = 'M5 12l5 5L19 7';
// A speech bubble with a phone: WhatsApp without its trademark.
const ICON_CHAT_PHONE = ['M3 21l1.7-5A8.5 8.5 0 1 1 8 19.3z', 'M9.5 9.5c.3 1.9 2.4 4 4.6 4.6l1-1.2 1.9.8-.4 1.6c-3.8.2-7.3-3.3-7.1-7.1l1.6-.4.8 1.9z'];
const ICON_UP = ['M7 10v11', 'M15 5.9L14 10h5.8a2 2 0 0 1 1.9 2.6l-2.3 8a2 2 0 0 1-1.9 1.4H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.8a2 2 0 0 0 1.8-1.1L12 2a3.1 3.1 0 0 1 3 3.9z'];
const ICON_DOWN = ['M17 14V3', 'M9 18.1l1-4.1H4.2a2 2 0 0 1-1.9-2.6l2.3-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.8a2 2 0 0 0-1.8 1.1L12 22a3.1 3.1 0 0 1-3-3.9z'];

export function mount(root, { config, siteKey, apiBase, locale, images }) {
  const t = translator(config.locale || locale, config.copy);
  const name = config.bot_name;
  // The chatbot's own contact settings (ADR-0110): extra fields of the
  // form, and WhatsApp — the app on a phone, WhatsApp Web elsewhere.
  const contactForm = normalizeContactForm(config.contact_form);
  const waUrl = whatsappUrl(config.whatsapp, t('whatsappGreeting', { site: '{site}' }), location.hostname);
  const whatsappLink = (className, label = t('whatsappOpen')) => {
    const link = el('a', className, { href: waUrl, target: '_blank', rel: 'noopener noreferrer' });
    link.appendChild(icon(ICON_CHAT_PHONE, 16));
    link.append(document.createTextNode(label));
    return link;
  };

  const sheet = document.createElement('style');
  sheet.textContent = styles({ accent: config.accent_color, position: config.position, font: FONT_FAMILY });

  // --- Panel ------------------------------------------------------------------
  const panel = el('section', 'panel', { role: 'dialog', 'aria-label': t('chatWith', { name }), tabindex: '-1' });
  panel.hidden = true;

  // Two rows (ADR-0089): who is talking and the close button on top, the
  // actions with visible labels underneath — icons alone were not understood
  // and squeezed the subtitle into four lines.
  const header = el('div', 'header');
  const top = el('div', 'header__top');
  const avatarUrl = config.avatar_url || images.avatar;
  if (avatarUrl) top.appendChild(withFallback(el('img', 'header__avatar', { src: avatarUrl, alt: '', width: '40', height: '40' }), images.avatar));
  const heading = el('div', 'header__text');
  const title = el('div', 'header__name');
  title.textContent = name;
  const subtitle = el('div', 'header__subtitle');
  subtitle.textContent = config.subtitle || t('subtitle');
  heading.append(title, subtitle);
  const close = el('button', 'icon-button header__close', { type: 'button', 'aria-label': t('closeChat') });
  close.appendChild(icon(ICON_CLOSE));
  top.append(heading, close);
  const action = (label, full, path) => {
    const node = el('button', 'header__action', { type: 'button', title: full });
    if (path) node.appendChild(icon(path, 16));
    node.append(document.createTextNode(label));
    return node;
  };
  // The visitor's own authorization and how to withdraw it (ADR-0089).
  const consentButton = action(t('consentAction'), t('consentMenu'), ICON_SHIELD);
  consentButton.setAttribute('aria-expanded', 'false');
  consentButton.setAttribute('aria-controls', 'chatbot-consent-menu');
  consentButton.hidden = true;
  // A person is always one click away, without a big button that teaches
  // visitors to skip the assistant (ADR-0065 §4).
  const person = action(t('personAction'), t('talkToPerson'), ICON_PERSON);
  person.hidden = !config.live_chat_enabled || config.mode === 'human_only';
  // Always there when the language has a number (ADR-0110): a link, it
  // leaves the page.
  const whatsappAction = el('a', 'header__action', { href: waUrl || '#', target: '_blank', rel: 'noopener noreferrer', title: t('whatsappOpen') });
  whatsappAction.appendChild(icon(ICON_CHAT_PHONE, 16));
  whatsappAction.append(document.createTextNode(t('whatsappAction')));
  whatsappAction.hidden = !waUrl;
  // Always there (ADR-0056): the visitor decides when a conversation ends.
  const restart = action(t('newAction'), t('newConversation'), ICON_NEW);
  // Only while talking with a person (ADR-0063).
  const endChat = action(t('endChat'), t('endChat'));
  endChat.hidden = true;
  // The visitor's own switch for the sound of a person's messages (ADR-0098).
  const sound = el('button', 'header__action header__action--icon', { type: 'button', 'aria-label': t('soundToggle') });
  sound.hidden = true;
  const showSound = () => {
    const on = !soundMuted();
    sound.setAttribute('aria-pressed', String(on));
    sound.title = on ? t('soundMute') : t('soundUnmute');
    sound.replaceChildren(icon(on ? ICON_BELL : ICON_BELL_OFF, 16));
  };
  showSound();
  sound.addEventListener('click', () => {
    setSoundMuted(!soundMuted());
    showSound();
  });
  const actions = el('div', 'header__actions');
  actions.append(consentButton, whatsappAction, person, restart, endChat, sound);
  // The row only takes room when it has something to offer.
  const syncActions = () => { actions.hidden = [...actions.children].every((node) => node.hidden); };
  const watchActions = new MutationObserver(syncActions);
  for (const node of actions.children) watchActions.observe(node, { attributes: true, attributeFilter: ['hidden'] });
  syncActions();
  header.append(top, actions);
  const consentMenu = el('div', 'consent-menu', { id: 'chatbot-consent-menu' });
  consentMenu.hidden = true;

  // Focusable so the keyboard can scroll it when it holds no button to reach.
  const log = el('div', 'log', { role: 'log', 'aria-live': 'polite', 'aria-label': t('conversationLog'), tabindex: '0' });
  const startLog = () => {
    log.replaceChildren();
    const today = el('div', 'log__day');
    today.textContent = t('today');
    log.appendChild(today);
    if (config.welcome_message) {
      const welcome = el('p', 'bubble bubble--bot');
      welcome.textContent = config.welcome_message;
      log.appendChild(welcome);
    }
  };
  startLog();

  const composer = el('form', 'composer');
  const inputId = 'chatbot-message';
  const label = el('label', 'visually-hidden', { for: inputId });
  label.textContent = t('message');
  const input = el('textarea', 'composer__input', { id: inputId, rows: '1', maxlength: String(MAX_LENGTH), placeholder: t('placeholder') });
  const send = el('button', 'composer__send', { type: 'submit', 'aria-label': t('send') });
  send.appendChild(icon(ICON_SEND));
  const counter = el('div', 'composer__counter', { 'aria-live': 'polite' });
  counter.hidden = true;
  // Dictation (ADR-0101): shown only while talking to the assistant (syncMic).
  const mic = el('button', 'composer__mic', { type: 'button', 'aria-label': t('micStart'), title: t('micStart') });
  mic.appendChild(icon(ICON_MIC));
  mic.hidden = true;
  // Only "recording", "10 seconds left" and "transcribing" are announced —
  // never the seconds as they pass. Visually, the bar below says it all.
  const micStatus = el('div', 'composer__status visually-hidden', { role: 'status' });
  // While recording the field becomes this bar: ✕ cancels, ✓ (where send
  // was) stops and transcribes into the field (ADR-0101, 2026-10-21).
  const rec = el('div', 'rec', { tabindex: '-1' });
  rec.hidden = true;
  const recCancel = el('button', 'rec__cancel', { type: 'button', 'aria-label': t('micCancel'), title: t('micCancel') });
  recCancel.appendChild(icon(ICON_CLOSE));
  const recDot = el('span', 'rec__dot', { 'aria-hidden': 'true' });
  const recLabel = el('span', 'rec__label');
  const recTime = el('span', 'rec__time', { 'aria-hidden': 'true' });
  const recBars = el('span', 'rec__bars', { 'aria-hidden': 'true' });
  for (let i = 0; i < 5; i += 1) recBars.appendChild(el('span'));
  const recDone = el('button', 'rec__done', { type: 'button', 'aria-label': t('micDone'), title: t('micDone') });
  recDone.appendChild(icon(ICON_CHECK));
  rec.append(recCancel, recDot, recLabel, recTime, recBars, recDone);
  composer.append(label, input, mic, send, counter, rec, micStatus);

  const disclaimer = el('div', 'disclaimer');
  disclaimer.textContent = t('disclaimer');
  // Kept after the text node: the live chat swaps `firstChild`'s text.
  const reportUrl = reportHref(config.report_url, window.location.origin, siteKey);
  if (reportUrl) {
    const report = el('a', 'disclaimer__report', { href: reportUrl, target: '_blank', rel: 'noopener', title: t('reportTitle') });
    report.textContent = t('report');
    disclaimer.append(document.createTextNode(' · '), report);
  }
  // During the trial and on Starter (decided by the backend, ADR-0060).
  if (config.show_branding && config.branding_label) {
    const branding = el('div', 'branding');
    branding.append(document.createTextNode(`${t('poweredBy')} `));
    const safeUrl = /^https:\/\//.test(config.branding_url || '') ? config.branding_url : null;
    const name = el(safeUrl ? 'a' : 'span', 'branding__name', safeUrl ? { href: safeUrl, target: '_blank', rel: 'noopener' } : {});
    name.textContent = config.branding_label;
    branding.appendChild(name);
    disclaimer.appendChild(branding);
  }

  // "Talk to a person" as a button only when a person is all there is (no
  // AI conversations left, ADR-0055 §4); otherwise the header icon and the
  // offers when they make sense (ADR-0065).
  const handoffBar = el('div', 'handoff-bar');
  const talk = el('button', 'handoff-bar__button', { type: 'button' });
  talk.textContent = t('talkToPerson');
  handoffBar.appendChild(talk);
  const barWanted = () => Boolean(config.live_chat_enabled) && config.mode === 'human_only';
  handoffBar.hidden = !barWanted();

  // "Email me the replies" lives here while a person attends, never in the
  // log, where new messages pushed it out of sight (ADR-0104).
  const emailBar = el('div', 'email-bar');
  emailBar.hidden = true;

  panel.append(header, consentMenu, log, handoffBar, emailBar, composer, disclaimer);

  // --- Launcher ---------------------------------------------------------------
  const launcher = createLauncher({
    config: { ...config, bot_name: name },
    t,
    images,
    storage: greetingMemory(),
    onOpen: () => setOpen(true),
  });

  root.append(sheet, launcher.element, panel);

  // A person's messages the visitor hasn't seen (ADR-0097): counted while the
  // panel is closed or the tab is out of view, cleared once both are back.
  const unread = unreadTracker({ onChange: (count, message) => launcher.notify(count, message), muted: soundMuted });
  const arm = () => unread.arm();
  root.addEventListener('pointerdown', arm);
  root.addEventListener('keydown', arm);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && !panel.hidden) unread.clear();
  });

  // The authorization the chatbot asks for before anything else (ADR-0089):
  // until the visitor gives it, nothing is sent — no session, no question.
  const asked = config.consent;
  const consentLocale = config.locale || locale || null;
  let consent = asked ? storedConsent(siteKey, asked.version, globalThis.localStorage, asked.versions) : null;
  const needsConsent = () => Boolean(asked) && !consent;
  let consentCard = null;

  // Earned in the background as soon as the panel opens (ADR-0057), with
  // the visitor's authorization when the chatbot asks for one.
  const session = chatSession(apiBase, siteKey, () => consent);

  function setOpen(open) {
    panel.hidden = !open;
    rememberOpen(siteKey, open);
    if (open) {
      launcher.hide();
      if (!document.hidden) unread.clear();
      if (needsConsent()) {
        askConsent();
        return;
      }
      session.get();
      // On a touch screen focusing the field opens the keyboard over the
      // conversation: there the dialog takes focus (a screen reader still
      // announces it) and the keyboard waits for a tap on the field.
      if (window.matchMedia('(pointer: coarse)').matches) panel.focus({ preventScroll: true });
      else input.focus();
    } else {
      launcher.show();
      launcher.focus();
    }
  }

  close.addEventListener('click', () => setOpen(false));
  root.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || panel.hidden) return;
    // Escape while recording cancels it, and only that.
    if (recording) {
      recording.cancel();
      return;
    }
    if (!consentMenu.hidden) {
      toggleConsentMenu(false);
      consentButton.focus();
      return;
    }
    setOpen(false);
  });

  // --- Composer ---------------------------------------------------------------
  const autosize = () => {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
    const length = input.value.length;
    counter.hidden = length < COUNTER_FROM;
    counter.textContent = t('tooLong', { count: length, max: MAX_LENGTH });
  };
  input.addEventListener('input', autosize);
  // Talking with a person: they see the visitor is writing (ADR-0066 §4).
  input.addEventListener('input', () => { if (live && input.value.trim()) live.typing(); });
  // Enter sends; Shift+Enter is a new line.
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      composer.requestSubmit();
    }
  });

  const scrollDown = () => { log.scrollTop = log.scrollHeight; };

  const appendBubble = (text, from) => {
    const bubble = el('p', `bubble bubble--${from}`);
    // Only the bot's replies are formatted; the visitor's text is shown as typed.
    if (from === 'bot') appendRichText(bubble, text);
    else bubble.textContent = text;
    log.appendChild(bubble);
    scrollDown();
    return bubble;
  };

  // The visitor's 👍/👎 under one answer (ADR-0102): pressing the chosen one
  // again takes it back. Shown at once, put back if the API refused it.
  const addVotes = (bubble, messageId, initial = null) => {
    let current = initial === 'up' || initial === 'down' ? initial : null;
    let busy = false;
    const row = el('div', 'votes', { role: 'group', 'aria-label': t('voteGroup') });
    const said = el('span', 'visually-hidden', { role: 'status' });
    const make = (vote, path, key) => {
      const node = el('button', 'vote', { type: 'button', 'aria-label': t(key), title: t(key) });
      node.appendChild(icon(path, 16));
      node.addEventListener('click', () => choose(vote));
      return node;
    };
    const up = make('up', ICON_UP, 'voteUp');
    const down = make('down', ICON_DOWN, 'voteDown');
    const show = () => {
      up.setAttribute('aria-pressed', String(current === 'up'));
      down.setAttribute('aria-pressed', String(current === 'down'));
    };
    async function choose(vote) {
      if (busy || !threadId) return;
      busy = true;
      const before = current;
      current = current === vote ? null : vote;
      show();
      const payload = { threadId, messageId, vote: current || 'none' };
      let outcome = await sendFeedback(apiBase, siteKey, await session.get(), payload);
      if (outcome === 'session' && !needsConsent()) {
        session.forget();
        outcome = await sendFeedback(apiBase, siteKey, await session.get(), payload);
      }
      busy = false;
      if (outcome === 'ok') {
        said.textContent = current ? t('voteThanks') : '';
        return;
      }
      current = before;
      show();
      // Refused for the authorization: the card already asks again.
      if (outcome !== 'consent') said.textContent = t('voteFailed');
    }
    show();
    row.append(up, down, said);
    bubble.after(row);
    return row;
  };

  // Shown from the moment a message is sent until the reply (or the error)
  // replaces it — without it, a slow model looked like a dead widget.
  const showTyping = () => {
    const typing = el('div', 'bubble bubble--bot typing', { role: 'status' });
    const text = el('span', 'visually-hidden');
    text.textContent = t('typing');
    const dots = el('span', 'typing__dots', { 'aria-hidden': 'true' });
    dots.append(el('span'), el('span'), el('span'));
    typing.append(text, dots);
    log.appendChild(typing);
    scrollDown();
    return typing;
  };

  // The design's error state: the message stays, marked "not sent", with an
  // alert and a retry that sends the same text again.
  const showFailure = (bubble, text, reason = config.error_message || t('error'), command = null) => {
    const wrapper = el('div', 'unsent');
    bubble.replaceWith(wrapper);
    bubble.classList.add('bubble--unsent');
    const note = el('div', 'unsent__note');
    note.textContent = t('notSent');
    wrapper.append(bubble, note);

    const alert = el('div', 'alert', { role: 'alert' });
    alert.appendChild(icon(ICON_ALERT));
    const body = el('div', 'alert__body');
    const message = el('div');
    message.textContent = reason;
    const retry = el('button', 'alert__retry', { type: 'button' });
    retry.textContent = t('retry');
    body.append(message, retry);
    alert.appendChild(body);
    log.appendChild(alert);
    scrollDown();

    retry.addEventListener('click', () => {
      alert.remove();
      wrapper.replaceWith(bubble);
      bubble.classList.remove('bubble--unsent');
      deliver(text, bubble, command);
    });
  };

  let threadId = loadThreadId(siteKey);
  // The earlier messages of this conversation were drawn (or no longer
  // should be: something was sent, or a new conversation started).
  let restored = false;
  let detectedLocale = null;
  let sending = false;
  // Once a human owns the conversation the bot stops trying — matches the
  // backend's own refusal (409). Reset only by a real live-chat mode, which
  // does not exist yet (chatbot-serverless).
  let handedOff = false;

  const setSending = (value) => {
    sending = value;
    send.disabled = value;
    send.setAttribute('aria-label', value ? t('sendWaiting') : t('send'));
  };

  async function deliver(text, bubble, command = null) {
    // The visitor was using the widget (the field or a button): a new
    // action button may take the focus; otherwise it stays where it was.
    const inWidget = Boolean(root.activeElement);
    let actionFocus = null;
    setSending(true);
    const typing = showTyping();
    let result = await sendMessage(apiBase, siteKey, text, threadId, locale, REPLY_TIMEOUT_MS, await session.get(), command);
    if (result.kind === 'session' && !needsConsent()) {
      session.forget();
      result = await sendMessage(apiBase, siteKey, text, threadId, locale, REPLY_TIMEOUT_MS, await session.get(), command);
    }
    typing.remove();

    if (needsConsent() || result.kind === 'consent') {
      // Asked again (withdrawn, or a new text): the question waits in the field.
      bubble.remove();
      input.value = text;
      autosize();
      setSending(false);
      askConsent(t('consentAgain'));
      return;
    }
    if (result.kind === 'reply') {
      threadId = result.threadId;
      detectedLocale = result.detectedLocale || detectedLocale;
      // Empty when the visitor asked for a person: only the offer (ADR-0065).
      if (result.reply) {
        const answer = appendBubble(result.reply, 'bot');
        if (result.messageId) addVotes(answer, result.messageId);
        // The answer gave the WhatsApp number: the link, built here (ADR-0110).
        if (result.whatsapp && waUrl) {
          log.appendChild(whatsappLink('card__button card__button--secondary whatsapp-link'));
          scrollDown();
        }
        lastQuestion = pendingQuestion;
      }
      if (result.action) actionFocus = showAction(result.action);
      if (result.handoffOffer && config.live_chat_enabled) offerFor(result.handoffOffer);
    }
    else if (result.kind === 'handoff') {
      // A person already has this conversation (another tab, a reload).
      const stored = storedLive(siteKey);
      if (stored) rejoin(stored);
      else {
        handedOff = true;
        syncMic();
        appendBubble(t('handoff'), 'bot');
      }
    }
    else if (result.kind === 'limit') {
      showFailure(bubble, text, t('tooMany'), command);
    }
    else {
      showFailure(bubble, text, undefined, command);
    }
    setSending(false);
    // They asked for a person and someone is there: now that this message
    // is done, connect (asking while it was "sending" was ignored).
    if (connectWhenDone) {
      connectWhenDone = false;
      askForPerson('visitor_requested');
    }
    if (actionFocus && inWidget) actionFocus.focus();
    else input.focus();
  }

  // --- Actions (ADR-0108): choice buttons and the summary to confirm ---------
  // Only the latest ones answer: anything sent disables the earlier ones.
  const disableActions = () => {
    for (const node of log.querySelectorAll('.action-options button, .action-card button')) node.disabled = true;
  };
  let actionCards = 0;

  // Returns the first new button, or null when nothing was drawn.
  function showAction(action) {
    if (action.state === 'asking' && action.options.length) {
      const row = el('div', 'action-options', { role: 'group', 'aria-label': t('actionOptions') });
      for (const option of action.options) {
        row.appendChild(button(option, () => ask(option), 'card__button card__button--secondary action-option'));
      }
      log.appendChild(row);
      scrollDown();
      return row.querySelector('button');
    }
    if (action.state === 'confirming' && action.summary.length) {
      const id = `action-card-${++actionCards}`;
      const node = card('action-card');
      node.setAttribute('role', 'group');
      node.setAttribute('aria-labelledby', id);
      const title = el('p', 'card__text action-card__title', { id });
      const words = action.labels || { review: t('actionSummary'), confirm: t('actionConfirm'), change: t('actionChange'), cancel: t('actionCancel') };
      title.textContent = action.name ? `${words.review} · ${action.name}` : words.review;
      const list = el('dl', 'action-card__list');
      for (const row of action.summary) {
        const term = el('dt');
        term.textContent = row.label;
        const value = el('dd');
        value.textContent = row.value;
        list.append(term, value);
      }
      const buttons = el('div', 'action-card__buttons');
      const confirm = button(words.confirm, () => ask(words.confirm, 'confirm'));
      buttons.append(
        confirm,
        button(words.change, () => ask(words.change, 'change'), 'card__button card__button--secondary'),
        button(words.cancel, () => ask(words.cancel, 'cancel'), 'card__button card__button--secondary'),
      );
      node.append(title, list, buttons);
      return confirm;
    }
    return null;
  }

  // A message to the assistant: typed, a choice button or a card's button.
  function ask(text, command = null) {
    if (!text || sending || live || handedOff) return;
    disableActions();
    // Back after a long pause: same conversation, marked so the visitor
    // knows the assistant still remembers the last few messages.
    if (isResuming(threadId, lastActivity(siteKey))) {
      const resumed = el('div', 'log__day');
      resumed.textContent = t('resumed');
      log.appendChild(resumed);
    }
    // A card's button is not a question for the team's message form.
    if (!command) pendingQuestion = text;
    restored = true;
    deliver(text, appendBubble(text, 'user'), command);
  }

  composer.addEventListener('submit', (event) => {
    event.preventDefault();
    const text = input.value.trim();
    if (!text || sending) return;
    if (live) {
      input.value = '';
      autosize();
      const bubble = appendBubble(text, 'user');
      const id = live.message(text);
      if (id) mine.add(id);
      else bubble.classList.add('bubble--unsent');
      return;
    }
    if (handedOff) return;
    input.value = '';
    autosize();
    ask(text);
  });

  restart.addEventListener('click', () => {
    if (sending || live) return;
    restored = true;
    forgetThread(siteKey);
    threadId = null;
    handedOff = false;
    syncMic();
    startLog();
    input.focus();
  });

  // --- Live chat with a person (ADR-0063) ------------------------------------
  let live = null;
  let drops = 0;
  // The visitor chose to leave a message while waiting in line.
  let leavingForMessage = false;
  // What they last asked: the message form starts with it. Asking for a
  // person is not a question for the team, so it doesn't replace it.
  let lastQuestion = '';
  let pendingQuestion = '';
  // Set by an offer to connect right away, once the reply is handled.
  let connectWhenDone = false;
  let statusNote = null;
  let advisorName = '';
  // Ids of this visitor's own messages: the service echoes them to every tab.
  const mine = new Set();
  const uiLocale = config.locale || locale || 'en';
  const visitorLocale = () => detectedLocale || locale || config.locale || 'en';
  const languageName = (code) => {
    try {
      return new Intl.DisplayNames([uiLocale], { type: 'language' }).of(code) || code;
    } catch {
      return code;
    }
  };

  const note = (text, replace = false) => {
    if (replace && statusNote) statusNote.remove();
    const node = el('div', 'note', { role: 'status' });
    node.textContent = text;
    log.appendChild(node);
    scrollDown();
    if (replace) statusNote = node;
    return node;
  };

  const card = (className) => {
    const node = el('div', `card ${className}`);
    log.appendChild(node);
    // Once the caller filled it: scrolling now would show an empty card.
    queueMicrotask(scrollDown);
    return node;
  };

  const button = (text, onClick, className = 'card__button') => {
    const node = el('button', className, { type: 'button' });
    node.textContent = text;
    node.addEventListener('click', onClick);
    return node;
  };

  // The hours in the visitor's time, for the visitor's language.
  const hoursLine = () => {
    const hours = describeHours(config.support_hours, visitorLocale(), uiLocale, new Date(), { allDay: t('allDay'), everyDay: (spans) => t('everyDay', { hours: spans }) });
    return hours ? t('hoursAre', { hours }) : '';
  };
  const whenText = (iso) => {
    const next = opening(iso, uiLocale);
    if (!next) return null;
    if (next.day === 'today') return t('todayAt', { time: next.time });
    if (next.day === 'tomorrow') return t('tomorrowAt', { time: next.time });
    return t('dayAt', { day: next.day, time: next.time });
  };
  const text = (className, content) => {
    const node = el('p', className);
    node.textContent = content;
    return node;
  };

  /**
   * What the backend says about a person (ADR-0065): connect right away
   * when they asked and someone is there; offer it when the assistant
   * couldn't help; otherwise say why nobody can and offer a message.
   */
  function offerFor(offer) {
    if (offer.state === 'available') {
      if (offer.reason === 'asked') {
        note(t('offerAsked'));
        connectWhenDone = true;
        return;
      }
      const node = card('offer card--pending');
      node.append(
        text('card__text', t('offerPerson')),
        text('card__hint', t('usuallyFast')),
        button(t('talkToPerson'), () => { node.remove(); askForPerson('low_confidence'); }),
        button(t('keepChatting'), () => { node.remove(); input.focus(); }, 'card__button card__button--secondary'),
      );
      return;
    }
    nobodyCard(offer.state === 'closed', offer.opens_at);
  }

  /** Nobody can attend: outside the hours, or nobody connected. */
  function nobodyCard(closed, opensAt) {
    const node = card('offer card--pending');
    const when = closed ? whenText(opensAt) : null;
    node.appendChild(text('card__text', closed ? (when ? t('outsideHours', { when }) : t('outsideHoursNoDate')) : t('teamOffline')));
    const hours = hoursLine();
    if (hours) node.appendChild(text('card__hint', hours));
    // The platform's support assistant opens a ticket instead (ADR-0078):
    // it already knows the client and the person.
    const support = config.audience === 'support';
    node.append(
      button(support ? t('createTicket') : t('leaveMessage'), () => { node.remove(); if (support) ticketForm(); else leaveForm(); }),
      // WhatsApp, when this language has a number (ADR-0110).
      ...(waUrl && !support ? [whatsappLink('card__button card__button--secondary')] : []),
      button(t('keepChatting'), () => { node.remove(); input.focus(); }, 'card__button card__button--secondary'),
    );
  }

  async function askForPerson(reason, chosen = null) {
    if (live || sending) return;
    if (needsConsent()) {
      askConsent();
      return;
    }
    // An earlier offer ("nobody is connected", "talk to a person?") no
    // longer holds once the visitor asks again: its buttons would act on
    // a state that is gone (found testing the live chat, 2026-10-16).
    for (const stale of log.querySelectorAll('.card--pending')) stale.remove();
    setSending(true);
    const options = { threadId, visitorLocale: visitorLocale(), locale: chosen, reason };
    let result = await requestHandoff(apiBase, siteKey, await session.get(), options);
    if (result.kind === 'session') {
      session.forget();
      result = await requestHandoff(apiBase, siteKey, await session.get(), options);
    }
    setSending(false);
    if (result.threadId) threadId = result.threadId;
    if (result.kind === 'connect') {
      enterLive();
      note(t('connecting'), true);
      live.start(result.url, result.ticket);
    }
    else if (result.kind === 'choose') {
      const choice = card('choice');
      const text = el('p', 'card__text');
      text.textContent = t('chooseLanguage');
      choice.appendChild(text);
      for (const code of result.locales) {
        choice.appendChild(button(t('continueIn', { language: languageName(code) }), () => { choice.remove(); askForPerson(reason, code); }));
      }
      choice.appendChild(button(t('leaveMessage'), () => { choice.remove(); leaveForm(); }, 'card__button card__button--secondary'));
    }
    else if (result.kind === 'unavailable') {
      nobodyCard(false, null);
    }
    else if (result.kind === 'closed') {
      nobodyCard(true, result.opensAt);
    }
    else if (result.kind === 'open') {
      const stored = storedLive(siteKey);
      if (stored) rejoin(stored);
      else note(t('handoff'));
    }
    else {
      note(t('couldNotConnect'));
    }
  }

  function leaveForm() {
    const form = el('form', 'card leave');
    const intro = el('p', 'card__text');
    intro.textContent = (contactForm && contactForm.intro) || t('leaveIntro');
    const hours = hoursLine();
    const field = (labelText, control) => {
      const wrapper = el('label', 'leave__field');
      const caption = el('span');
      caption.textContent = labelText;
      wrapper.append(caption, control);
      return wrapper;
    };
    const nameInput = el('input', 'leave__input', { type: 'text', required: '', maxlength: '100', autocomplete: 'name' });
    const emailError = el('p', 'leave__error', { id: `leave-email-error-${Date.now()}`, hidden: '' });
    emailError.textContent = t('emailCannotReceive');
    const emailInput = el('input', 'leave__input', { type: 'email', required: '', maxlength: '254', autocomplete: 'email', 'aria-describedby': emailError.id });
    const messageInput = el('textarea', 'leave__input', { required: '', maxlength: '2000', rows: '3' });
    messageInput.value = lastQuestion;
    // The chatbot's own fields (ADR-0110), after the three of always.
    const stamp = Date.now();
    const extras = (contactForm ? contactForm.fields : []).map((spec) => {
      const { tag, attrs } = controlFor(spec);
      const errorNode = el('p', 'leave__error', { id: `leave-${spec.key}-error-${stamp}`, hidden: '' });
      errorNode.textContent = t('fieldInvalid');
      const control = el(tag, 'leave__input', { ...attrs, ...(spec.required ? { required: '' } : {}), 'aria-describedby': errorNode.id });
      if (spec.type === 'choice') {
        const empty = el('option', '', { value: '' });
        empty.textContent = t('chooseOption');
        control.appendChild(empty);
        for (const option of spec.options) {
          const node = el('option', '', { value: option.value });
          node.textContent = option.label;
          control.appendChild(node);
        }
      }
      return { spec, control, errorNode };
    });
    const submit = el('button', 'card__button', { type: 'submit' });
    submit.textContent = t('sendMessage');
    const extraNodes = extras.flatMap(({ spec, control, errorNode }) => [field(spec.required ? spec.label : t('optionalField', { label: spec.label }), control), errorNode]);
    form.append(intro, ...(hours ? [text('card__hint', hours)] : []), field(t('yourName'), nameInput), field(t('yourEmail'), emailInput), emailError, field(t('yourMessage'), messageInput), ...extraNodes, submit);
    log.appendChild(form);
    scrollDown();
    nameInput.focus();
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      submit.disabled = true;
      const extra = Object.fromEntries(extras.map(({ spec, control }) => [spec.key, control.value.trim()]));
      const fields = { threadId, name: nameInput.value.trim(), email: emailInput.value.trim(), message: messageInput.value.trim(), visitorLocale: visitorLocale(), fields: extra };
      emailError.hidden = true;
      emailInput.removeAttribute('aria-invalid');
      for (const { control, errorNode } of extras) {
        errorNode.hidden = true;
        control.removeAttribute('aria-invalid');
      }
      let outcome = await leaveMessage(apiBase, siteKey, await session.get(), fields);
      // Only a refused session earns a new one: a limit is waited out.
      if (outcome === 'session') {
        session.forget();
        outcome = await leaveMessage(apiBase, siteKey, await session.get(), fields);
      }
      if (outcome === 'sent') {
        form.remove();
        note(t('messageSent', { email: fields.email }));
        return;
      }
      submit.disabled = false;
      const invalid = outcome.startsWith('invalid_field:') ? extras.find(({ spec }) => `invalid_field:${spec.key}` === outcome) : null;
      if (outcome === 'invalid_email') {
        emailError.hidden = false;
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
      }
      else if (invalid) {
        invalid.errorNode.hidden = false;
        invalid.control.setAttribute('aria-invalid', 'true');
        invalid.control.focus();
      }
      else {
        note(t(outcome === 'limit' ? 'tooManyMessages' : 'couldNotConnect'));
      }
    });
  }

  function ticketForm() {
    const form = el('form', 'card leave');
    const intro = el('p', 'card__text');
    intro.textContent = t('ticketIntro');
    const caption = el('span');
    caption.textContent = t('yourMessage');
    const messageInput = el('textarea', 'leave__input', { required: '', maxlength: '2000', rows: '4' });
    messageInput.value = lastQuestion;
    const wrapper = el('label', 'leave__field');
    wrapper.append(caption, messageInput);
    const submit = el('button', 'card__button', { type: 'submit' });
    submit.textContent = t('createTicket');
    form.append(intro, wrapper, submit);
    log.appendChild(form);
    scrollDown();
    messageInput.focus();
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      submit.disabled = true;
      const fields = { threadId, message: messageInput.value.trim() };
      let ticket = await openSupportTicket(apiBase, siteKey, await session.get(), fields);
      if (!ticket) {
        session.forget();
        ticket = await openSupportTicket(apiBase, siteKey, await session.get(), fields);
      }
      if (!ticket) {
        submit.disabled = false;
        note(t('couldNotCreateTicket'));
        return;
      }
      form.remove();
      const done = card('offer');
      done.appendChild(text('card__text', t('ticketCreated', { id: ticket.id })));
      if (/^https?:\/\//.test(ticket.url || '')) {
        const link = el('a', 'card__button', { href: ticket.url, target: '_top' });
        link.textContent = t('seeTicket');
        done.appendChild(link);
      }
    });
  }

  // The person attending lost their connection (ADR-0104): one note, gone
  // when they are back, when someone else joins or when it ends.
  let awayNode = null;
  function personAway(name) {
    if (awayNode) return;
    awayNode = card('offer');
    awayNode.setAttribute('role', 'status');
    awayNode.append(
      text('card__text', t('advisorAway', { name: name || advisorName })),
      button(t('leaveMessage'), () => { personBack(); leavingForMessage = true; if (live) live.end(); }, 'card__button card__button--secondary'),
    );
  }
  function personBack() {
    if (awayNode) awayNode.remove();
    awayNode = null;
  }

  function personName(name) {
    advisorName = name;
    subtitle.textContent = t('withPerson', { name });
  }

  function enterLive() {
    composer.hidden = false;
    handoffBar.hidden = true;
    person.hidden = true;
    restart.hidden = true;
    endChat.hidden = false;
    sound.hidden = false;
    live = liveConnection(siteKey, {
      'session.ready': (frame) => {
        drops = 0;
        if (frame.advisor) personName(frame.advisor.display_name);
        if (frame.status === 'human') disclaimer.firstChild.textContent = t('personDisclaimer');
        // Already delivered before the reload: not new.
        for (const message of frame.messages || []) showLiveMessage(message, true);
        // A rejoin never throws away an address being typed.
        if (!(emailForm && !frame.email_saved)) emailRow(Boolean(frame.email_saved));
        if (frame.status === 'human' && statusNote) { statusNote.remove(); statusNote = null; }
        if (frame.status === 'human' && frame.advisor_away) personAway();
        else personBack();
      },
      'conversation.status': (frame) => {
        if (frame.status !== 'waiting_advisor') return;
        personBack();
        const left = frame.reason === 'advisor_left';
        const waiting = note(left ? t('advisorLeft', { name: advisorName }) : frame.queue_position > 1 ? t('inLine', { n: frame.queue_position }) : t('connecting'), true);
        // Waiting is a choice: a message instead, any time (ADR-0065 §5).
        waiting.appendChild(button(t('leaveInstead'), () => {
          leavingForMessage = true;
          if (live) live.end();
        }, 'card__button card__button--secondary note__button'));
      },
      typing: (frame) => {
        if (frame.from !== 'advisor') return;
        personTyping(frame.display_name || advisorName);
      },
      // The person who joined hasn't written yet (ADR-0066 §4).
      'advisor.slow': (frame) => {
        const node = card('offer');
        node.append(
          text('card__text', t('advisorSlow', { name: frame.display_name || advisorName })),
          text('card__hint', t('advisorSlowHint')),
          button(t('keepWaiting'), () => { node.remove(); input.focus(); }, 'card__button card__button--secondary'),
          button(t('leaveMessage'), () => { node.remove(); leavingForMessage = true; if (live) live.end(); }),
        );
      },
      'advisor.away': (frame) => personAway(frame.display_name),
      'advisor.back': (frame) => {
        if (!awayNode) return;
        personBack();
        note(t('advisorBack', { name: frame.display_name || advisorName }));
      },
      'advisor.joined': (frame) => {
        personBack();
        personName(frame.advisor.display_name);
        // Only now: while waiting in line, nobody is talking with them yet.
        disclaimer.firstChild.textContent = t('personDisclaimer');
        note(t('joined', { name: frame.advisor.display_name }), true);
      },
      message: (frame) => showLiveMessage(frame.message),
      'visitor.email_saved': (frame) => emailRow(frame.saved),
      error: (frame) => {
        if (frame.code === 'invalid_frame' && emailForm) emailForm.invalid();
      },
      'inactivity.warning': () => note(t('stillThere')),
      'conversation.closed': (frame) => {
        if (leavingForMessage) {
          leavingForMessage = false;
          if (statusNote) { statusNote.remove(); statusNote = null; }
          exitLive(true);
          leaveForm();
          return;
        }
        const texts = {
          visitor_closed: t('endedByYou'),
          advisor_closed: t('endedByAdvisor', { name: advisorName }),
          inactivity: t('endedIdle'),
          no_advisor: t('endedNoAdvisor'),
        };
        note(texts[frame.reason] || t('endedLeft'), true);
        exitLive();
      },
      drop: async () => {
        note(t('reconnecting'), true);
        // A refused rejoin looks like a network error: after a couple of
        // tries, ask whether the conversation is still open at all.
        drops += 1;
        if (drops === 2 && live) {
          const status = await liveStatus(apiBase, siteKey, await session.get(), (storedLive(siteKey) || {}).conversationId);
          if (status === 'bot' && live) { note(t('endedLeft'), true); exitLive(); }
        }
      },
      lost: () => { note(t('endedLeft'), true); exitLive(); },
    });
    syncMic();
  }

  // "Luis is writing…", while frames keep coming (one every 3 s at most).
  let typingNode = null;
  let typingTimer = null;
  function personTyping(name) {
    if (!typingNode) {
      typingNode = el('div', 'bubble bubble--bot typing', { role: 'status' });
      const label = el('span', 'typing__label');
      const dots = el('span', 'typing__dots', { 'aria-hidden': 'true' });
      dots.append(el('span'), el('span'), el('span'));
      typingNode.append(label, dots);
    }
    typingNode.firstChild.textContent = t('personTyping', { name });
    log.appendChild(typingNode);
    scrollDown();
    clearTimeout(typingTimer);
    typingTimer = setTimeout(stopPersonTyping, 6000);
  }
  function stopPersonTyping() {
    clearTimeout(typingTimer);
    if (typingNode) typingNode.remove();
  }

  function showLiveMessage(message, replayed = false) {
    if (message.from === 'advisor') stopPersonTyping();
    if (message.from === 'visitor') {
      // Already on screen if this tab sent it.
      if (mine.has(message.id)) return;
      mine.add(message.id);
      appendBubble(message.text, 'user');
      return;
    }
    if (message.from !== 'advisor') return;
    const wrapper = el('div', 'person');
    const who = el('div', 'person__name');
    who.textContent = message.advisor_display_name || '';
    const bubble = el('p', 'bubble bubble--bot bubble--person');
    bubble.textContent = message.text;
    wrapper.append(who, bubble);
    log.appendChild(wrapper);
    scrollDown();
    if (!replayed && (panel.hidden || document.hidden)) unread.add(message);
  }

  // "Get the reply by email if I leave" (ADR-0097): one row in the live chat,
  // the offer or what was saved. The address never comes back from the service.
  let emailNode = null;
  let emailForm = null;
  function emailRow(saved) {
    if (!live) return;
    const fresh = el('div', 'note email-offer');
    if (saved) {
      fresh.appendChild(text('note__text', t('emailSaved')));
      fresh.appendChild(button(t('emailRemove'), () => { if (live) live.email(''); }, 'link-button'));
    }
    else {
      fresh.appendChild(button(t('emailOffer'), () => openEmailForm(fresh), 'link-button'));
    }
    emailBar.replaceChildren(fresh);
    emailBar.hidden = false;
    emailNode = fresh;
    emailForm = null;
  }

  function openEmailForm(row) {
    const form = el('form', 'email-offer__form', { novalidate: '' });
    const errorId = `email-offer-error-${Date.now()}`;
    const field = el('label', 'leave__field');
    const caption = el('span');
    caption.textContent = t('yourEmail');
    const input = el('input', 'leave__input', { type: 'email', required: '', maxlength: '254', autocomplete: 'email', 'aria-describedby': `${errorId}-hint ${errorId}` });
    field.append(caption, input);
    const hint = text('card__hint', t('emailOfferHint'));
    hint.id = `${errorId}-hint`;
    const error = el('p', 'leave__error', { id: errorId, hidden: '' });
    error.textContent = t('emailInvalid');
    const save = el('button', 'card__button', { type: 'submit' });
    save.textContent = t('emailSave');
    const cancel = button(t('emailCancel'), () => emailRow(false), 'card__button card__button--secondary');
    form.append(field, hint, error, save, cancel);
    row.replaceChildren(form);
    input.focus();
    const invalid = () => {
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      save.disabled = false;
      input.focus();
    };
    emailForm = { invalid };
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!input.checkValidity() || !input.value.trim()) { invalid(); return; }
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      save.disabled = true;
      if (!live || !live.email(input.value.trim())) invalid();
    });
  }

  function exitLive(quiet = false) {
    if (live) live.close();
    live = null;
    if (emailNode) emailNode.remove();
    emailNode = null;
    emailBar.hidden = true;
    personBack();
    emailForm = null;
    handedOff = false;
    endChat.hidden = true;
    sound.hidden = true;
    restart.hidden = false;
    handoffBar.hidden = !barWanted();
    person.hidden = !config.live_chat_enabled || config.mode === 'human_only';
    subtitle.textContent = config.subtitle || t('subtitle');
    disclaimer.firstChild.textContent = t('disclaimer');
    if (config.mode === 'human_only') {
      composer.hidden = true;
      restart.hidden = true;
    }
    else if (!quiet) {
      note(t('backWithBot'));
    }
    syncMic();
  }

  function rejoin(stored) {
    if (live) return;
    enterLive();
    note(t('reconnecting'), true);
    live.rejoin(stored);
  }

  // No AI conversations left (ADR-0055 §4): a person, or nothing.
  const humanOnly = config.mode === 'human_only';
  const botComposer = (enabled) => {
    composer.hidden = !enabled && !live;
    restart.hidden = !enabled || Boolean(live);
  };
  if (humanOnly) botComposer(false);

  talk.addEventListener('click', () => askForPerson('visitor_requested'));
  person.addEventListener('click', () => askForPerson('visitor_requested'));
  endChat.addEventListener('click', () => { if (live) live.end(); });

  // --- Dictation (ADR-0101) ----------------------------------------------------
  // Only with the assistant: never with a person, before the authorization, or
  // with no AI conversations left. The text lands in the field; the visitor sends.
  const voiceOn = config.voice_input && voiceSupported(window);
  let micBlocked = false;
  let recording = null;
  let transcribing = false;
  function syncMic() {
    const show = voiceOn && !micBlocked && !composer.hidden && !live && !handedOff && !needsConsent() && !humanOnly;
    if (!show && recording) recording.cancel();
    mic.hidden = !show;
  }
  const still = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  // 'recording', 'transcribing' or '' — the composer shows the field or the bar.
  function recLook(state) {
    composer.classList.toggle('composer--recording', state === 'recording');
    composer.classList.toggle('composer--transcribing', state === 'transcribing');
    rec.hidden = !state;
    recLabel.textContent = state === 'transcribing' ? t('micTranscribing') : t('micRecording');
    recTime.textContent = state === 'recording' ? clock(0) : '';
    micStatus.textContent = recLabel.textContent && state ? recLabel.textContent : '';
  }
  let barsFrame = 0;
  function animateBars() {
    cancelAnimationFrame(barsFrame);
    if (!recording || still()) return;
    const history = [0, 0, 0, 0, 0];
    let last = 0;
    const step = (now) => {
      if (!recording) return;
      if (now - last > 90) {
        last = now;
        history.shift();
        history.push(recording.level());
        history.forEach((level, i) => { recBars.children[i].style.transform = `scaleY(${0.2 + level * 0.8})`; });
      }
      barsFrame = requestAnimationFrame(step);
    };
    barsFrame = requestAnimationFrame(step);
  }
  const onTick = (left) => {
    const elapsed = MAX_SECONDS - left;
    recTime.textContent = left <= 10 ? t('micLeft', { seconds: left }) : clock(elapsed);
    if (left === 10) micStatus.textContent = t('micTenLeft');
  };
  recCancel.addEventListener('click', () => { if (recording) recording.cancel(); });
  recDone.addEventListener('click', () => { if (recording) recording.stop(); });
  mic.addEventListener('click', async () => {
    if (transcribing || recording) return;
    try {
      recording = await startRecording({ onTick });
    } catch {
      // Refused, or no microphone: no second prompt in this visit.
      recording = null;
      micBlocked = true;
      syncMic();
      note(t('micDenied'));
      input.focus();
      return;
    }
    recLook('recording');
    recDone.focus();
    animateBars();
    const audio = await recording.done;
    recording = null;
    cancelAnimationFrame(barsFrame);
    if (!audio) {
      recLook('');
      // Cancelled: back where they started, unless the panel moved on.
      if (!mic.hidden) mic.focus();
      return;
    }
    transcribing = true;
    recLook('transcribing');
    rec.focus();
    let result = await transcribe(apiBase, siteKey, await session.get(), audio, uiLocale);
    if (result.kind === 'session' && !needsConsent()) {
      session.forget();
      result = await transcribe(apiBase, siteKey, await session.get(), audio, uiLocale);
    }
    transcribing = false;
    recLook('');
    // Refused for the authorization: the card already asks again.
    if (result.kind === 'consent') return;
    if (result.kind === 'text' && result.text) {
      const before = input.value.trimEnd();
      input.value = (before ? `${before} ${result.text}` : result.text).slice(0, MAX_LENGTH);
      autosize();
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
      return;
    }
    note(t(result.kind === 'text' ? 'micNothing' : result.kind === 'limit' ? 'micLimit' : 'micError'));
    input.focus();
  });

  // --- Authorization (ADR-0089) ----------------------------------------------
  function hideConsentCard() {
    if (consentCard) consentCard.remove();
    consentCard = null;
  }

  /** The card that asks — or, after a "no" in this tab, says why there is no chat. */
  function askConsent(lead = '', declinedNow = wasDeclined(siteKey, asked.version)) {
    toggleConsentMenu(false);
    consentButton.hidden = true;
    composer.hidden = true;
    handoffBar.hidden = true;
    person.hidden = true;
    restart.hidden = true;
    syncMic();
    hideConsentCard();
    const node = el('div', 'card consent', { role: 'group', 'aria-labelledby': 'chatbot-consent-title', tabindex: '-1' });
    const heading = el('h2', 'visually-hidden', { id: 'chatbot-consent-title' });
    heading.textContent = t('consentTitle');
    node.appendChild(heading);
    if (lead) node.appendChild(text('card__text', lead));
    if (declinedNow) {
      node.append(
        text('card__text', t('consentDeclined')),
        button(t('consentChange'), () => askConsent('', false), 'card__button card__button--secondary'),
      );
    }
    else {
      node.append(
        renderConsentHtml(el('div', 'consent__message'), asked.message_html),
        button(asked.accept_label || t('consentAccept'), acceptConsent),
        button(asked.decline_label || t('consentDecline'), () => {
          rememberDeclined(siteKey, asked.version);
          declineConsent(apiBase, siteKey, asked.version, consentLocale);
          askConsent('', true);
        }),
      );
    }
    log.appendChild(node);
    consentCard = node;
    scrollDown();
    if (!panel.hidden) node.focus({ preventScroll: true });
  }

  function acceptConsent() {
    consent = rememberConsent(siteKey, { id: newId(), version: asked.version, locale: consentLocale, at: new Date().toISOString() });
    rememberDeclined(siteKey, null);
    hideConsentCard();
    consentButton.hidden = false;
    // A fresh session that carries it.
    session.forget();
    session.get();
    composer.hidden = humanOnly;
    restart.hidden = humanOnly;
    handoffBar.hidden = !barWanted();
    person.hidden = !config.live_chat_enabled || humanOnly;
    syncMic();
    resume();
    if (!panel.hidden && !composer.hidden) input.focus();
    else if (!panel.hidden) panel.focus({ preventScroll: true });
  }

  /** Withdrawn, or refused by the API: forget it and ask again. */
  function loseConsent(lead) {
    consent = null;
    forgetConsent(siteKey);
    session.forget();
    if (live) {
      live.end();
      exitLive(true);
    }
    askConsent(lead, false);
  }

  function toggleConsentMenu(open) {
    consentMenu.hidden = !open;
    consentButton.setAttribute('aria-expanded', String(open));
    if (!open || !consent) return;
    const when = new Date(consent.at);
    const date = Number.isNaN(when.getTime()) ? '' : when.toLocaleDateString(uiLocale, { year: 'numeric', month: 'long', day: 'numeric' });
    const id = el('span', 'consent-menu__id');
    id.textContent = consent.id;
    const idLine = el('p', 'consent-menu__line');
    const [before, after = ''] = t('consentId', { id: '\u0000' }).split('\u0000');
    idLine.append(document.createTextNode(before), id, document.createTextNode(after));
    const withdraw = button(t('consentWithdraw'), async () => {
      withdraw.disabled = true;
      const withdrawnId = consent.id;
      const token = await session.get();
      // Refused as consent_required: already handled (asked again).
      if (!consent) return;
      let done = await withdrawConsent(apiBase, siteKey, token);
      if (!consent) return;
      if (!done && token) {
        session.forget();
        done = await withdrawConsent(apiBase, siteKey, await session.get());
      }
      if (!done) {
        withdraw.disabled = false;
        note(t('consentWithdrawFailed'));
        return;
      }
      // The visitor keeps the ID: it is their proof of the withdrawal too.
      loseConsent(t('consentWithdrawn', { id: withdrawnId }));
    }, 'card__button card__button--secondary');
    consentMenu.replaceChildren(
      text('consent-menu__title', t('consentMenu')),
      text('consent-menu__line', date ? t('consentGiven', { date }) : ''),
      idLine,
      withdraw,
    );
    withdraw.focus();
  }
  consentButton.addEventListener('click', () => toggleConsentMenu(consentMenu.hidden));
  // Any request refused with consent_required (ADR-0089).
  whenRequired(() => { if (asked && !consentCard) loseConsent(t('consentAgain')); });

  // Back on the page during a conversation with a person: rejoin it — if it
  // is still open (it may have closed while the visitor was away).
  let stored = config.live_chat_enabled ? storedLive(siteKey) : null;
  /** What a returning visitor had: drawn or rejoined once they may chat. */
  function resume() {
    // Another page of the same conversation with the assistant: draw what the
    // person already saw (ADR-0074).
    if (!stored && threadId) {
      fetchConversation(apiBase, siteKey, threadId).then((messages) => {
        // Nothing sent meanwhile, and no new conversation asked for.
        if (restored || !messages.length) return;
        restored = true;
        for (const message of messages) {
          const bubble = appendBubble(message.text, message.role === 'user' ? 'user' : 'bot');
          if (message.role === 'bot' && typeof message.message_id === 'string') addVotes(bubble, message.message_id, message.vote);
        }
      });
    }
    if (stored) {
      const rejoining = stored;
      stored = null;
      session.get().then((token) => liveStatus(apiBase, siteKey, token, rejoining.conversationId)).then((status) => {
        if (status === 'bot') {
          forgetLive(siteKey);
          return;
        }
        if (needsConsent()) return;
        setOpen(true);
        rejoin(rejoining);
      });
    }
  }

  syncMic();
  const reopen = !stored && wasOpen(siteKey);
  if (!config.live_chat_enabled) forgetLive(siteKey);
  if (needsConsent()) {
    // Nothing is asked of the API until the visitor accepts.
    if (reopen || stored) setOpen(true);
  }
  else {
    if (asked) consentButton.hidden = false;
    resume();
    if (reopen) setOpen(true);
  }

  return { setOpen };
}
